import { options } from "../util";
import { UserContext } from "../../../../../../context/UserContext";
import { FlowHandlersContext } from "../../../../Menu/FlowContainer";
import { Box, Stack, Typography } from "@mui/material";
import { Field } from "formik";
import { FormContainer, NavButtons } from "../../../../Menu/Flow";
import employerFlowStyles from "../../styles";
import { styles } from "../../../../../../legacyComponents/componentsV2/FlowV2/Fields/util";
import { useContext, useState } from "react";

export default function ReimbursementTiming(props) {
  const { user, getEmployer, getEmployerPlan } = useContext(UserContext);
  const flowHandlers = useContext(FlowHandlersContext);
  const { mode, exitUrl, step, formik, displayOnly, employerPlanId } = props;
  const { touched, values, setFieldValue } = formik;

  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const isSalusion = values?.reimbursementMethod?.value === "SALUSION";
  const timingOptions = isSalusion
    ? options.reimbursementTimings
    : options.reportTimings;
  const heading = isSalusion
    ? "Choose how often Salusion should debit your company’s bank account."
    : "Choose when to receive reimbursement reports.";
  const employerPlan = getEmployerPlan(employerPlanId);
  const planType = employerPlan?.planType || values?.planType;
  const allowanceDay = planType === "ICHRA" ? "12th" : "17th";
  const firstDebitDay = planType === "ICHRA" ? "13th" : "18th";

  // Membership in the active list, not just "is something set" — MONTHLY
  // appears in both reimbursementTimings and reportTimings and means
  // different things in each.
  const isValid = timingOptions.some(
    (o) => o.value === values?.reimbursementTiming?.value,
  );
  const showError =
    (hasAttemptedSubmit || touched.reimbursementTiming) && !isValid;

  async function handleNext(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    setHasAttemptedSubmit(true);

    if (!isValid) {
      return false;
    }

    await formik.submitForm();

    if (flowHandlers?.onNext) {
      await flowHandlers.onNext(formik.values);
    }

    return false;
  }

  return (
    <Stack spacing="30px" height="100%">
      <FormContainer>
        <Typography variant="h2" sx={employerFlowStyles.h2}>
          Reimbursement Timing
        </Typography>
        <Typography>{heading}</Typography>

        <Stack spacing={2} sx={{ marginTop: "20px" }}>
          <Stack>
            <Field name="reimbursementTiming">
              {({ field }) => (
                <select
                  {...field}
                  value={values.reimbursementTiming?.value || ""}
                  onChange={(e) => {
                    const selectedOption = timingOptions.find(
                      (opt) => opt.value === e.target.value,
                    );
                    setFieldValue(
                      "reimbursementTiming",
                      selectedOption || options.nullOption(),
                    );
                    setFieldValue("reimbursementDay", 0);
                  }}
                  disabled={displayOnly}
                  style={{
                    ...styles.select(!field.value.value),
                    border: `1px solid ${showError ? "#dc2626" : "#ACBEE8"}`,
                  }}
                >
                  <option value="">
                    {isSalusion
                      ? "Select an ACH debit schedule"
                      : "Select a report schedule"}
                  </option>
                  {timingOptions?.map((option) => (
                    <option key={option.value} value={option.value}>
                      {isSalusion && option.value === "MONTHLY"
                        ? "Twice monthly — 5th and 22nd"
                        : option.display}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            {showError && (
              <div className="error-message-fields">
                Please select a schedule before continuing
              </div>
            )}

            {!isSalusion && isValid && (
              <Box
                sx={{
                  mt: 2.5,
                  p: 2,
                  backgroundColor: "#f8f9fc",
                  border: "1px solid #ACBEE8",
                  borderRadius: "8px",
                }}
              >
                <Typography sx={{ fontWeight: 600, marginBottom: "8px" }}>
                  Important
                </Typography>
                <Typography sx={{ fontSize: "14px" }}>
                  Your first reimbursement report will be ready on the first day
                  of the plan year, helping you reimburse employees sooner.
                </Typography>
                <Typography sx={{ fontSize: "14px", marginTop: "8px" }}>
                  Salusion makes allowances available on the {allowanceDay} of
                  the preceding month. If a premium expense is submitted and
                  approved before the first day of the plan year, the available
                  allowance will be allocated to that expense, and the
                  reimbursement amount will appear on your first report.
                </Typography>
              </Box>
            )}

            {isSalusion && values?.reimbursementTiming?.value === "DAILY" && (
              <>
                <Typography
                  sx={{
                    marginTop: "20px",
                    fontSize: "14px",
                    fontWeight: 400,
                    lineHeight: 1.6,
                    color: "#465578",
                  }}
                >
                  Employees generally receive their reimbursements approximately
                  five business days after each debit.
                </Typography>
                <Box
                  sx={{
                    marginTop: "20px",
                    p: 2,
                    backgroundColor: "#f8f9fc",
                    border: "1px solid #ACBEE8",
                    borderRadius: "8px",
                  }}
                >
                  <Typography sx={{ fontWeight: 600, marginBottom: "8px" }}>
                    Important
                  </Typography>
                  <Typography sx={{ fontSize: "14px" }}>
                    The ACH schedule is designed to help employees receive
                    premium reimbursements around the time their premiums are
                    due. For the first month of the plan year, this means your
                    first debit may occur before the plan starts.
                  </Typography>
                  <Typography sx={{ fontSize: "14px", marginTop: "8px" }}>
                    Salusion makes allowances available on the {allowanceDay} of
                    the preceding month. If a premium expense is submitted and
                    approved before the first day of the plan year, the available
                    allowance will be allocated to that expense, and debit
                    processing may begin as early as the {firstDebitDay}.
                  </Typography>
                </Box>
              </>
            )}

            {isSalusion && values?.reimbursementTiming?.value === "MONTHLY" && (
              <>
                <Typography
                  sx={{
                    marginTop: "20px",
                    fontSize: "14px",
                    fontWeight: 400,
                    lineHeight: 1.6,
                    color: "#465578",
                  }}
                >
                  Employees generally receive their reimbursements approximately
                  five business days after each debit.
                </Typography>
                <Box
                  sx={{
                    marginTop: "20px",
                    p: 2,
                    backgroundColor: "#f8f9fc",
                    border: "1px solid #ACBEE8",
                    borderRadius: "8px",
                  }}
                >
                  <Typography sx={{ fontWeight: 600, marginBottom: "8px" }}>
                    Important
                  </Typography>
                  <Typography sx={{ fontSize: "14px" }}>
                    The ACH schedule is designed to help employees receive
                    premium reimbursements around the time their premiums are
                    due. For the first month of the plan year, this means your
                    first debit may occur before the plan starts.
                  </Typography>
                  <Typography sx={{ fontSize: "14px", marginTop: "8px" }}>
                    Salusion makes allowances available on the {allowanceDay} of
                    the preceding month. If a premium expense is submitted and
                    approved before the first day of the plan year, the available
                    allowance will be allocated to that expense, and debit
                    processing may begin as early as the 22nd.
                  </Typography>
                </Box>
              </>
            )}
          </Stack>
        </Stack>
      </FormContainer>

      <NavButtons
        mode={mode}
        enableSave={true}
        exitUrl={exitUrl}
        step={step}
        onNextStep={handleNext}
      />
    </Stack>
  );
}
